import React, {useState, useEffect} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Image,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import i18n from '../i18n';
import {MATERIAL_ICONS_FONT} from '../styles/fonts';
import {SUPPORTED_LOCALES, SupportedLocale} from '../i18n';
import {useAppDispatch, useAppSelector} from '../store';
import {setUser, logout, clearAuth} from '../store/authSlice';
import {settingsService, authService} from '../services';
import {api} from '../services/api';
import {KeyboardAware} from '../components/KeyboardAware';
import {clearFrameCache} from '../services/offlineCache';
import {QrCodeResponse} from '../types';
import {useSettingsNavigation} from '../hooks/useSettingsNavigation';
import {
  SettingsLayout,
  GlassPanel,
  Heading,
  SubHeading,
  BodyText,
  Button,
  theme,
} from '../components/DarkTheme';
import OfflineBanner from '../components/OfflineBanner';
import {useNetworkStatus} from '../hooks/useNetworkStatus';

export default function AccountSettingsScreen() {
  const {t} = useTranslation();
  const isOnline = useNetworkStatus();
  const {navigateToSettingsScreen} = useSettingsNavigation('AccountSettings');
  const dispatch = useAppDispatch();
  const {user} = useAppSelector(state => state.auth);

  const [displayName, setDisplayName] = useState(user?.displayName || 'Oma');
  const [email, setEmail] = useState(user?.email || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [qrCode, setQrCode] = useState<QrCodeResponse | null>(null);
  const [isGeneratingQr, setIsGeneratingQr] = useState(false);
  const [qrError, setQrError] = useState<string | null>(null);

  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  const [currentLocale, setCurrentLocale] = useState<SupportedLocale>(
    (i18n.language as SupportedLocale) || 'en'
  );
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Load locale from settings on mount
  useEffect(() => {
    const loadLocale = async () => {
      try {
        const settings = await settingsService.getSettings();
        if (settings.locale && SUPPORTED_LOCALES.includes(settings.locale as SupportedLocale)) {
          setCurrentLocale(settings.locale as SupportedLocale);
          await i18n.changeLanguage(settings.locale);
        }
      } catch (error) {
        console.error('Failed to load locale', error);
      }
    };
    loadLocale();
  }, []);

  const handleLanguageChange = async (locale: SupportedLocale) => {
    if (locale === currentLocale) return;

    setIsChangingLanguage(true);
    try {
      await settingsService.updateSettings({locale});
      await i18n.changeLanguage(locale);
      setCurrentLocale(locale);
    } catch (error) {
      console.error('Failed to change language', error);
    } finally {
      setIsChangingLanguage(false);
    }
  };

  const handleLogout = async () => {
    await dispatch(logout());
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      await settingsService.deleteAccount();
      // After deletion, clear local auth state (don't call logout API - session is already deleted)
      await api.clearToken();
      await clearFrameCache();
      await i18n.changeLanguage('en');
      dispatch(clearAuth());
    } catch (error: any) {
      setDeleteError(error.message || t('account.deleteError'));
      setIsDeleting(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const trimmedEmail = email.trim();
      const updatedUser = await settingsService.updateUser({
        displayName: displayName.trim() || undefined,
        email: trimmedEmail || undefined,
        clearEmail: trimmedEmail === '' && user?.email != null,
      });
      dispatch(setUser(updatedUser));
      // Update local state to match saved values
      setDisplayName(updatedUser.displayName);
      setEmail(updatedUser.email || '');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: any) {
      setSaveError(error.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateQr = async () => {
    setIsGeneratingQr(true);
    setQrError(null);
    setQrCode(null);

    try {
      const response = await authService.generateQrCode();
      setQrCode(response);
    } catch (error: any) {
      setQrError(error.message || 'Failed to generate QR code');
    } finally {
      setIsGeneratingQr(false);
    }
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    setResendError(null);
    setResendMessage(null);

    try {
      await settingsService.resendVerificationEmail();
      setResendMessage('Verification email sent!');
      setTimeout(() => setResendMessage(null), 5000);
    } catch (error: any) {
      setResendError(error.message || 'Failed to send verification email');
      setTimeout(() => setResendError(null), 5000);
    } finally {
      setIsResending(false);
    }
  };

  const hasChanges =
    displayName !== user?.displayName || email !== (user?.email || '');

  return (
    <SettingsLayout currentScreen="account" onNavigate={navigateToSettingsScreen} onLogout={handleLogout}>
      <KeyboardAware>
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <OfflineBanner />

        <View style={styles.header}>
          <View style={styles.headerIconBg}>
            <Text style={styles.headerIcon} accessible={false}>person</Text>
          </View>
          <Heading>{t('account.title')}</Heading>
          <BodyText style={styles.subtitle}>
            {t('account.subtitle')}
          </BodyText>
        </View>

        {/* Profile Section */}
        <View style={styles.section}>
          <SubHeading style={styles.sectionTitle}>{t('account.profile')}</SubHeading>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>{t('account.displayName')}</Text>
            <TextInput
              style={styles.textInput}
              value={displayName}
              onChangeText={setDisplayName}
              placeholder="Oma"
              placeholderTextColor={theme.textMuted}
              accessibilityLabel={t('account.displayName')}
            />
            <Text style={styles.inputHint}>
              {t('account.displayNameHint')}
            </Text>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>{t('account.emailAddress')}</Text>
            <TextInput
              style={styles.textInput}
              value={email}
              onChangeText={setEmail}
              placeholder="your@email.com"
              placeholderTextColor={theme.textMuted}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              accessibilityLabel={t('account.emailAddress')}
            />
            {user?.email && !user.emailVerified && (
              <View style={styles.verificationRow}>
                <View style={styles.unverifiedBadge}>
                  <Text style={styles.badgeIcon} accessible={false}>warning</Text>
                  <Text style={styles.unverifiedText}>{t('account.notVerified')}</Text>
                </View>
                <TouchableOpacity
                  onPress={handleResendVerification}
                  disabled={isResending || !isOnline}
                  style={[styles.resendButton, !isOnline && {opacity: 0.5}]}
                  accessibilityLabel={t('account.resendEmail')}
                  accessibilityRole="button">
                  {isResending ? (
                    <ActivityIndicator size="small" color={theme.primary} />
                  ) : (
                    <Text style={styles.resendButtonText}>{t('account.resendEmail')}</Text>
                  )}
                </TouchableOpacity>
              </View>
            )}
            {resendMessage && (
              <Text style={styles.successText}>{resendMessage}</Text>
            )}
            {resendError && (
              <Text style={styles.errorTextSmall}>{resendError}</Text>
            )}
            {user?.email && user.emailVerified && (
              <View style={styles.verifiedBadge}>
                <Text style={styles.verifiedIcon} accessible={false}>check_circle</Text>
                <Text style={styles.verifiedText}>{t('common.verified')}</Text>
              </View>
            )}
            <Text style={styles.inputHint}>
              {t('account.emailHint')}
            </Text>
          </View>

          {saveError && <Text style={styles.errorText}>{saveError}</Text>}
          {saveSuccess && (
            <View style={styles.successBanner}>
              <Text style={styles.successIcon} accessible={false}>check_circle</Text>
              <Text style={styles.successBannerText}>{t('account.changesSaved')}</Text>
            </View>
          )}

          {hasChanges && (
            <Button
              label={t('account.saveChanges')}
              icon="save"
              onPress={handleSave}
              disabled={isSaving || !isOnline}
            />
          )}
        </View>

        {/* QR Code Section */}
        <View style={styles.section}>
          <SubHeading style={styles.sectionTitle}>{t('account.loginFromDevice')}</SubHeading>
          <BodyText style={styles.sectionDescription}>
            {t('account.qrDescription')}
          </BodyText>

          {qrError && <Text style={styles.errorText}>{qrError}</Text>}

          {qrCode ? (
            <GlassPanel centered style={styles.qrContainer}>
              <View style={styles.qrImageWrapper}>
                <Image
                  source={{uri: qrCode.qrCodeDataUrl}}
                  style={styles.qrImage}
                  resizeMode="contain"
                />
              </View>
              <View style={styles.qrExpiryBadge}>
                <Text style={styles.qrExpiryIcon} accessible={false}>schedule</Text>
                <Text style={styles.qrExpiryText}>
                  {t('account.expiresAt', {
                    time: new Date(qrCode.expiresAt).toLocaleTimeString(undefined, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  })}
                </Text>
              </View>
              <TouchableOpacity
                style={styles.regenerateButton}
                onPress={handleGenerateQr}
                activeOpacity={0.7}
                accessibilityLabel={t('account.generateNewCode')}
                accessibilityRole="button">
                <Text style={styles.regenerateIcon} accessible={false}>refresh</Text>
                <Text style={styles.regenerateButtonText}>{t('account.generateNewCode')}</Text>
              </TouchableOpacity>
            </GlassPanel>
          ) : (
            <GlassPanel centered style={styles.qrPlaceholder}>
              <Text style={styles.qrPlaceholderIcon} accessible={false}>qr_code_2</Text>
              <BodyText style={styles.qrPlaceholderText}>
                {t('account.scanToLogin')}
              </BodyText>
              <Button
                label={isGeneratingQr ? t('account.generating') : t('account.generateQr')}
                icon="qr_code"
                onPress={handleGenerateQr}
                disabled={isGeneratingQr || !isOnline}
              />
            </GlassPanel>
          )}
        </View>

        {/* Language Section */}
        <View style={styles.section}>
          <SubHeading style={styles.sectionTitle}>{t('account.language')}</SubHeading>
          <BodyText style={styles.sectionDescription}>
            {t('account.languageHint')}
          </BodyText>

          <View style={styles.languageOptions}>
            <TouchableOpacity
              style={[
                styles.languageOption,
                currentLocale === 'en' && styles.languageOptionSelected,
                !isOnline && {opacity: 0.5},
              ]}
              onPress={() => handleLanguageChange('en')}
              disabled={isChangingLanguage || !isOnline}
              activeOpacity={0.7}
              accessibilityLabel={t('languages.en')}
              accessibilityRole="radio"
              accessibilityState={{checked: currentLocale === 'en'}}>
              <Text style={styles.languageFlag}>EN</Text>
              <Text style={[
                styles.languageLabel,
                currentLocale === 'en' && styles.languageLabelSelected,
              ]}>
                {t('languages.en')}
              </Text>
              {currentLocale === 'en' && (
                <Text style={styles.languageCheck} accessible={false}>check</Text>
              )}
              {isChangingLanguage && currentLocale !== 'en' && (
                <ActivityIndicator size="small" color={theme.primary} />
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.languageOption,
                currentLocale === 'de' && styles.languageOptionSelected,
                !isOnline && {opacity: 0.5},
              ]}
              onPress={() => handleLanguageChange('de')}
              disabled={isChangingLanguage || !isOnline}
              activeOpacity={0.7}
              accessibilityLabel={t('languages.de')}
              accessibilityRole="radio"
              accessibilityState={{checked: currentLocale === 'de'}}>
              <Text style={styles.languageFlag}>DE</Text>
              <Text style={[
                styles.languageLabel,
                currentLocale === 'de' && styles.languageLabelSelected,
              ]}>
                {t('languages.de')}
              </Text>
              {currentLocale === 'de' && (
                <Text style={styles.languageCheck} accessible={false}>check</Text>
              )}
              {isChangingLanguage && currentLocale !== 'de' && (
                <ActivityIndicator size="small" color={theme.primary} />
              )}
            </TouchableOpacity>
          </View>
        </View>

        {/* Delete Account Section */}
        <View style={[styles.section, styles.dangerSection]}>
          <SubHeading style={styles.dangerTitle}>{t('account.dangerZone')}</SubHeading>

          {!showDeleteConfirm ? (
            <TouchableOpacity
              style={[styles.deleteButton, !isOnline && {opacity: 0.5}]}
              onPress={() => setShowDeleteConfirm(true)}
              disabled={!isOnline}
              activeOpacity={0.7}
              accessibilityLabel={t('account.deleteAccount')}
              accessibilityRole="button">
              <Text style={styles.deleteButtonIcon} accessible={false}>delete_forever</Text>
              <Text style={styles.deleteButtonText}>{t('account.deleteAccount')}</Text>
            </TouchableOpacity>
          ) : (
            <GlassPanel style={styles.deleteConfirmPanel}>
              <Text style={styles.deleteConfirmIcon} accessible={false}>warning</Text>
              <Text style={styles.deleteConfirmTitle}>{t('account.confirmDeleteTitle')}</Text>
              <BodyText style={styles.deleteConfirmText}>
                {t('account.confirmDeleteText')}
              </BodyText>

              {deleteError && (
                <Text style={styles.errorText}>{deleteError}</Text>
              )}

              <View style={styles.deleteConfirmButtons}>
                <TouchableOpacity
                  style={styles.cancelButton}
                  onPress={() => {
                    setShowDeleteConfirm(false);
                    setDeleteError(null);
                  }}
                  disabled={isDeleting}
                  activeOpacity={0.7}
                  accessibilityLabel={t('common.cancel')}
                  accessibilityRole="button">
                  <Text style={styles.cancelButtonText}>{t('common.cancel')}</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.confirmDeleteButton}
                  onPress={handleDeleteAccount}
                  disabled={isDeleting}
                  activeOpacity={0.7}
                  accessibilityLabel={t('account.deleteForever')}
                  accessibilityRole="button">
                  {isDeleting ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : (
                    <>
                      <Text style={styles.confirmDeleteIcon} accessible={false}>delete_forever</Text>
                      <Text style={styles.confirmDeleteText}>{t('account.deleteForever')}</Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            </GlassPanel>
          )}
        </View>
      </ScrollView>
      </KeyboardAware>
    </SettingsLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 24,
    maxWidth: 600,
    alignSelf: 'center',
    width: '100%',
  },

  // Header
  header: {
    alignItems: 'center',
    marginBottom: 32,
  },
  headerIconBg: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: theme.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 32,
    color: theme.primary,
  },
  subtitle: {
    color: theme.textMuted,
    textAlign: 'center',
    marginTop: 8,
  },

  // Section
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    marginBottom: 16,
  },
  sectionDescription: {
    color: theme.textMuted,
    marginBottom: 16,
  },

  // Input Group
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.text,
    marginBottom: 8,
  },
  textInput: {
    fontSize: 16,
    color: theme.text,
    backgroundColor: theme.surface,
    borderWidth: 1,
    borderColor: theme.borderLight,
    borderRadius: 12,
    padding: 16,
    minHeight: 56,
  } as any,
  inputHint: {
    fontSize: 13,
    color: theme.textMuted,
    marginTop: 8,
    fontStyle: 'italic',
  },

  // Verification
  verificationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    gap: 16,
  },
  unverifiedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(251, 191, 36, 0.15)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  badgeIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 16,
    color: '#fbbf24',
  },
  unverifiedText: {
    fontSize: 13,
    color: '#fbbf24',
    fontWeight: '500',
  },
  resendButton: {
    minWidth: 100,
  },
  resendButtonText: {
    fontSize: 14,
    color: theme.primary,
    fontWeight: '600',
  },
  verifiedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
  },
  verifiedIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 18,
    color: theme.success,
  },
  verifiedText: {
    fontSize: 13,
    color: theme.success,
    fontWeight: '500',
  },

  // Messages
  errorTextSmall: {
    fontSize: 13,
    color: theme.error,
    marginTop: 8,
  },
  errorText: {
    fontSize: 14,
    color: theme.error,
    marginBottom: 16,
  },
  successText: {
    fontSize: 13,
    color: theme.success,
    marginTop: 8,
  },
  successBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: 'rgba(74, 222, 128, 0.15)',
    padding: 12,
    borderRadius: 12,
    marginBottom: 16,
  },
  successIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 20,
    color: theme.success,
  },
  successBannerText: {
    fontSize: 14,
    color: theme.success,
    fontWeight: '500',
  },

  // QR Code
  qrContainer: {
    marginTop: 8,
  },
  qrImageWrapper: {
    backgroundColor: '#ffffff',
    padding: 16,
    borderRadius: 12,
    marginBottom: 16,
  },
  qrImage: {
    width: 200,
    height: 200,
  },
  qrExpiryBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 16,
  },
  qrExpiryIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 18,
    color: theme.textMuted,
  },
  qrExpiryText: {
    fontSize: 14,
    color: theme.textMuted,
  },
  regenerateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  regenerateIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 20,
    color: theme.primary,
  },
  regenerateButtonText: {
    fontSize: 14,
    color: theme.primary,
    fontWeight: '600',
  },
  qrPlaceholder: {
    marginTop: 8,
  },
  qrPlaceholderIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 64,
    color: theme.textMuted,
    marginBottom: 16,
  },
  qrPlaceholderText: {
    color: theme.textMuted,
    textAlign: 'center',
    marginBottom: 24,
    maxWidth: 280,
  },

  // Language
  languageOptions: {
    gap: 12,
  },
  languageOption: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.surface,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: theme.borderLight,
    gap: 12,
  } as any,
  languageOptionSelected: {
    borderColor: theme.primary,
    backgroundColor: 'rgba(255, 158, 128, 0.1)',
  },
  languageFlag: {
    fontSize: 16,
    fontWeight: '700',
    color: theme.text,
    backgroundColor: theme.borderLight,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    overflow: 'hidden',
  },
  languageLabel: {
    flex: 1,
    fontSize: 16,
    color: theme.text,
  },
  languageLabelSelected: {
    fontWeight: '600',
    color: theme.primary,
  },
  languageCheck: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.primary,
  },

  // Danger Zone / Delete Account
  dangerSection: {
    marginTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(239, 68, 68, 0.2)',
    paddingTop: 32,
  },
  dangerTitle: {
    color: theme.error,
    marginBottom: 16,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.3)',
    borderRadius: 12,
    padding: 16,
  } as any,
  deleteButtonIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.error,
  },
  deleteButtonText: {
    fontSize: 16,
    color: theme.error,
    fontWeight: '600',
  },
  deleteConfirmPanel: {
    backgroundColor: 'rgba(239, 68, 68, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.2)',
  },
  deleteConfirmIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 48,
    color: theme.error,
    marginBottom: 16,
  },
  deleteConfirmTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: theme.error,
    marginBottom: 8,
    textAlign: 'center',
  },
  deleteConfirmText: {
    color: theme.textMuted,
    textAlign: 'center',
    marginBottom: 24,
  },
  deleteConfirmButtons: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  } as any,
  cancelButton: {
    flex: 1,
    backgroundColor: theme.surface,
    borderWidth: 1,
    borderColor: theme.borderLight,
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    color: theme.text,
    fontWeight: '600',
  },
  confirmDeleteButton: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: theme.error,
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  } as any,
  confirmDeleteIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 20,
    color: '#fff',
  },
  confirmDeleteText: {
    fontSize: 16,
    color: '#fff',
    fontWeight: '600',
  },
});
